import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type AnnouncementProviderContext = {
  addAnnouncement: (announcement: string) => void;
};

const AnnouncementContext = createContext<AnnouncementProviderContext | undefined>(undefined);

type Announcements = { announcement: string; key: number }[];

type Props = {
  children: ReactNode;
};

const AnnouncementProvider = ({ children }: Props) => {
  const [announcements, setAnnouncements] = useState<Announcements>([]);

  const addAnnouncement = useCallback((announcement: string) => {
    setAnnouncements(previousAnnouncements => [...previousAnnouncements, { announcement, key: Math.random() }]);
  }, []);

  useEffect(() => {
    const clearAnnouncements = () => {
      if (announcements.length) {
        setAnnouncements([{ announcement: '', key: Math.random() }]);
      }
    };

    document.addEventListener('focus', clearAnnouncements, true);

    return () => {
      document.removeEventListener('focus', clearAnnouncements, true);
    };
  }, [announcements.length]);

  const value = useMemo(() => ({ addAnnouncement }), [addAnnouncement]);

  // Map over each message and render it separately, otherwise screen readers will announce all the messages instead of
  // only the new one. Each message needs to be wrapped in a div, otherwise JAWS announces them all.
  return (
    <AnnouncementContext.Provider value={value}>
      <div aria-live="polite" aria-atomic="false" className="hide-text" data-test="announcements">
        {announcements.map(({ announcement, key }) => announcement && <div key={key}>{announcement}</div>)}
      </div>

      {children}
    </AnnouncementContext.Provider>
  );
};

const useAnnouncementContext = () => {
  const context = useContext(AnnouncementContext);

  if (context === undefined) {
    throw new Error('useAnnouncementContext must be used within AnnouncementProvider');
  }

  return context;
};

export type { AnnouncementProviderContext };
export { AnnouncementProvider, useAnnouncementContext };
